import { useState, useMemo } from "react";

// ─── Shared helpers ───────────────────────────────────────────────────────────
const fmt  = (n, dec = 2) => n.toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtD = (n) => "$" + fmt(n);
const fmtP = (n) => fmt(n, 2) + "%";

function Field({ label, value, onChange, prefix, suffix, type = "number", min, max, step = "any", note }) {
  return (
    <div style={{ marginBottom: "18px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "7px" }}>
        <label style={{ fontSize: "11px", color: "var(--text-muted)", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace" }}>{label}</label>
        {note && <span style={{ fontSize: "11px", color: "var(--text-faint)" }}>{note}</span>}
      </div>
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        {prefix && <span style={{ position: "absolute", left: "13px", color: "var(--text-muted)", fontSize: "14px", pointerEvents: "none" }}>{prefix}</span>}
        <input
          type={type} value={value} min={min} max={max} step={step}
          onChange={e => onChange(e.target.value)}
          style={{
            width: "100%", padding: `11px ${suffix ? "40px" : "14px"} 11px ${prefix ? "28px" : "14px"}`,
            background: "var(--bg-deepest)", border: "1px solid var(--border-strong)", borderRadius: "10px",
            color: "var(--text-primary)", fontSize: "14px", fontFamily: "'Capsule Sans', sans-serif",
            outline: "none", boxSizing: "border-box",
            transition: "border-color 0.2s",
          }}
          onFocus={e => e.target.style.borderColor = "#D4AF37"}
          onBlur={e => e.target.style.borderColor = "var(--border-strong)"}
        />
        {suffix && <span style={{ position: "absolute", right: "13px", color: "var(--text-muted)", fontSize: "13px", pointerEvents: "none" }}>{suffix}</span>}
      </div>
    </div>
  );
}

function ResultRow({ label, value, highlight }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "12px 16px", borderRadius: "10px",
      background: highlight ? "rgba(212,175,55,0.08)" : "transparent",
      border: highlight ? "1px solid rgba(212,175,55,0.2)" : "1px solid transparent",
      marginBottom: "8px",
    }}>
      <span style={{ fontSize: "13px", color: "var(--text-muted)" }}>{label}</span>
      <span style={{ fontSize: highlight ? "18px" : "15px", fontWeight: highlight ? "800" : "600", color: highlight ? "#D4AF37" : "var(--text-primary)", letterSpacing: "-0.02em", fontFamily: "'Capsule Sans', sans-serif" }}>{value}</span>
    </div>
  );
}

function Card({ children }) {
  return (
    <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-strong)", borderRadius: "16px", padding: "28px" }}>
      {children}
    </div>
  );
}

function CalcGrid({ children }) {
  return (
    <div className="calc-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", alignItems: "start" }}>
      {children}
    </div>
  );
}

function ResultsCard({ title, children }) {
  return (
    <Card>
      <div style={{ fontSize: "11px", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.12em", marginBottom: "20px" }}>{title}</div>
      {children}
    </Card>
  );
}

// ─── 1. Mortgage Calculator ───────────────────────────────────────────────────
function MortgageCalc() {
  const [price,    setPrice]    = useState(400000);
  const [down,     setDown]     = useState(80000);
  const [rate,     setRate]     = useState(7.0);
  const [term,     setTerm]     = useState(30);
  const [tax,      setTax]      = useState(350);
  const [ins,      setIns]      = useState(120);

  const res = useMemo(() => {
    const principal = Math.max(0, Number(price) - Number(down));
    const r = Number(rate) / 100 / 12;
    const n = Number(term) * 12;
    if (r === 0) { const mp = principal / n; return { mp, total: mp * n, interest: 0, principal, piti: mp + Number(tax) + Number(ins) }; }
    const mp = principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    return {
      mp, piti: mp + Number(tax) + Number(ins),
      total: mp * n, interest: mp * n - principal,
      principal, dp: Number(down) / Number(price) * 100,
    };
  }, [price, down, rate, term, tax, ins]);

  return (
    <CalcGrid>
      <Card>
        <Field label="Home Price"        value={price}  onChange={setPrice}  prefix="$" />
        <Field label="Down Payment"      value={down}   onChange={setDown}   prefix="$" note={res.dp ? fmtP(res.dp) + " down" : ""} />
        <Field label="Interest Rate"     value={rate}   onChange={setRate}   suffix="%" step="0.01" />
        <Field label="Loan Term"         value={term}   onChange={setTerm}   suffix="yrs" step="1" min="1" max="30" />
        <Field label="Property Tax /mo"  value={tax}    onChange={setTax}    prefix="$" />
        <Field label="Home Insurance /mo"value={ins}    onChange={setIns}    prefix="$" />
      </Card>
      <ResultsCard title="MONTHLY BREAKDOWN">
        <ResultRow label="Monthly Payment (P&I)" value={fmtD(res.mp)}   highlight />
        <ResultRow label="Total PITI Payment"    value={fmtD(res.piti)} />
        <ResultRow label="Loan Amount"           value={fmtD(res.principal)} />
        <ResultRow label="Total Interest Paid"   value={fmtD(res.interest)} />
        <ResultRow label="Total Cost of Loan"    value={fmtD(res.total)} />
        <div style={{ marginTop: "16px", background: "var(--bg-main)", borderRadius: "10px", padding: "14px" }}>
          <div style={{ fontSize: "11px", color: "var(--text-muted)", marginBottom: "10px", fontFamily: "'JetBrains Mono', monospace" }}>PRINCIPAL VS INTEREST</div>
          <div style={{ height: "10px", borderRadius: "5px", background: "var(--bg-hover)", overflow: "hidden", display: "flex" }}>
            <div style={{ width: `${res.principal / res.total * 100}%`, background: "linear-gradient(90deg, #D4AF37, #9A7C1E)", transition: "width 0.4s ease" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px", fontSize: "11px", color: "var(--text-muted)" }}>
            <span>Principal {fmtP(res.principal / res.total * 100)}</span>
            <span>Interest {fmtP(res.interest / res.total * 100)}</span>
          </div>
        </div>
      </ResultsCard>
    </CalcGrid>
  );
}

// ─── 2. Loan Calculator ───────────────────────────────────────────────────────
function LoanCalc() {
  const [amount, setAmount] = useState(25000);
  const [rate,   setRate]   = useState(8.5);
  const [term,   setTerm]   = useState(5);

  const res = useMemo(() => {
    const r = Number(rate) / 100 / 12;
    const n = Number(term) * 12;
    const p = Number(amount);
    if (r === 0) { const mp = p / n; return { mp, total: mp * n, interest: 0 }; }
    const mp = p * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    return { mp, total: mp * n, interest: mp * n - p };
  }, [amount, rate, term]);

  return (
    <CalcGrid>
      <Card>
        <Field label="Loan Amount"   value={amount} onChange={setAmount} prefix="$" />
        <Field label="Interest Rate" value={rate}   onChange={setRate}   suffix="%" step="0.01" />
        <Field label="Loan Term"     value={term}   onChange={setTerm}   suffix="yrs" step="1" min="1" max="30" />
      </Card>
      <ResultsCard title="LOAN SUMMARY">
        <ResultRow label="Monthly Payment"  value={fmtD(res.mp)}       highlight />
        <ResultRow label="Total Repayment"  value={fmtD(res.total)}    />
        <ResultRow label="Total Interest"   value={fmtD(res.interest)} />
        <ResultRow label="Interest / Principal Ratio" value={fmt(res.interest / Number(amount) * 100) + "%"} />
      </ResultsCard>
    </CalcGrid>
  );
}

// ─── 3. Auto Loan Calculator ──────────────────────────────────────────────────
function AutoLoanCalc() {
  const [price,    setPrice]    = useState(35000);
  const [down,     setDown]     = useState(5000);
  const [tradeIn,  setTradeIn]  = useState(0);
  const [rate,     setRate]     = useState(6.5);
  const [term,     setTerm]     = useState(60);
  const [salesTax, setSalesTax] = useState(8.0);

  const res = useMemo(() => {
    const taxAmt  = Number(price) * Number(salesTax) / 100;
    const principal = Math.max(0, Number(price) + taxAmt - Number(down) - Number(tradeIn));
    const r = Number(rate) / 100 / 12;
    const n = Number(term);
    if (r === 0) { const mp = principal / n; return { mp, total: mp * n, interest: 0, principal, taxAmt }; }
    const mp = principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    return { mp, total: mp * n, interest: mp * n - principal, principal, taxAmt };
  }, [price, down, tradeIn, rate, term, salesTax]);

  return (
    <CalcGrid>
      <Card>
        <Field label="Vehicle Price"  value={price}    onChange={setPrice}    prefix="$" />
        <Field label="Down Payment"   value={down}     onChange={setDown}     prefix="$" />
        <Field label="Trade-In Value" value={tradeIn}  onChange={setTradeIn}  prefix="$" />
        <Field label="Sales Tax"      value={salesTax} onChange={setSalesTax} suffix="%" step="0.1" />
        <Field label="Interest Rate"  value={rate}     onChange={setRate}     suffix="%" step="0.01" />
        <Field label="Loan Term"      value={term}     onChange={setTerm}     suffix="mos" step="1" min="12" max="84" />
      </Card>
      <ResultsCard title="AUTO LOAN SUMMARY">
        <ResultRow label="Monthly Payment"   value={fmtD(res.mp)}        highlight />
        <ResultRow label="Amount Financed"   value={fmtD(res.principal)} />
        <ResultRow label="Sales Tax"         value={fmtD(res.taxAmt)}    />
        <ResultRow label="Total Interest"    value={fmtD(res.interest)}  />
        <ResultRow label="Total Cost"        value={fmtD(res.total + Number(down) + Number(tradeIn))} />
      </ResultsCard>
    </CalcGrid>
  );
}

// ─── 4. Interest Calculator ───────────────────────────────────────────────────
function InterestCalc() {
  const [principal, setPrincipal] = useState(10000);
  const [rate,      setRate]      = useState(5.0);
  const [years,     setYears]     = useState(10);
  const [compound,  setCompound]  = useState("12");

  const FREQ = [
    { label: "Annually",    value: "1"   },
    { label: "Semi-Annually",value:"2"   },
    { label: "Quarterly",   value: "4"   },
    { label: "Monthly",     value: "12"  },
    { label: "Daily",       value: "365" },
  ];

  const res = useMemo(() => {
    const p = Number(principal), r = Number(rate) / 100, t = Number(years), n = Number(compound);
    const fv = p * Math.pow(1 + r / n, n * t);
    const fvSimple = p * (1 + r * t);
    return { fv, interest: fv - p, fvSimple, interestSimple: fvSimple - p };
  }, [principal, rate, years, compound]);

  return (
    <CalcGrid>
      <Card>
        <Field label="Principal Amount" value={principal} onChange={setPrincipal} prefix="$" />
        <Field label="Annual Rate"      value={rate}      onChange={setRate}      suffix="%" step="0.01" />
        <Field label="Time Period"      value={years}     onChange={setYears}     suffix="yrs" step="1" min="1" />
        <div style={{ marginBottom: "18px" }}>
          <label style={{ display: "block", fontSize: "11px", color: "var(--text-muted)", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace", marginBottom: "7px" }}>Compound Frequency</label>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {FREQ.map(f => (
              <button key={f.value} onClick={() => setCompound(f.value)} style={{
                padding: "7px 12px", borderRadius: "8px", fontSize: "12px", cursor: "pointer",
                background: compound === f.value ? "rgba(212,175,55,0.15)" : "var(--bg-deepest)",
                color: compound === f.value ? "#D4AF37" : "var(--text-muted)",
                border: compound === f.value ? "1px solid rgba(212,175,55,0.3)" : "1px solid var(--border-strong)",
                fontFamily: "'Capsule Sans', sans-serif", transition: "all 0.15s",
              }}>{f.label}</button>
            ))}
          </div>
        </div>
      </Card>
      <ResultsCard title="INTEREST RESULTS">
        <ResultRow label="Future Value (Compound)" value={fmtD(res.fv)}           highlight />
        <ResultRow label="Interest Earned"         value={fmtD(res.interest)}      />
        <ResultRow label="Simple Interest FV"      value={fmtD(res.fvSimple)}      />
        <ResultRow label="Compound Advantage"      value={fmtD(res.interest - res.interestSimple)} />
        <ResultRow label="Total Return"            value={fmt((res.fv / Number(principal) - 1) * 100) + "%"} />
      </ResultsCard>
    </CalcGrid>
  );
}

// ─── 5. Payment Calculator ────────────────────────────────────────────────────
function PaymentCalc() {
  const [payment, setPayment] = useState(500);
  const [rate,    setRate]    = useState(7.0);
  const [term,    setTerm]    = useState(5);

  const res = useMemo(() => {
    const r = Number(rate) / 100 / 12;
    const n = Number(term) * 12;
    const pmt = Number(payment);
    if (r === 0) return { principal: pmt * n, total: pmt * n, interest: 0 };
    const principal = pmt * (1 - Math.pow(1 + r, -n)) / r;
    return { principal, total: pmt * n, interest: pmt * n - principal };
  }, [payment, rate, term]);

  return (
    <CalcGrid>
      <Card>
        <Field label="Monthly Payment"  value={payment} onChange={setPayment} prefix="$" />
        <Field label="Interest Rate"    value={rate}    onChange={setRate}    suffix="%" step="0.01" />
        <Field label="Loan Term"        value={term}    onChange={setTerm}    suffix="yrs" step="1" min="1" max="30" />
        <div style={{ marginTop: "8px", background: "var(--bg-main)", borderRadius: "10px", padding: "14px", fontSize: "12px", color: "var(--text-muted)", lineHeight: "1.6" }}>
          Enter your desired monthly payment to see how much you can borrow at a given rate and term.
        </div>
      </Card>
      <ResultsCard title="BORROWING POWER">
        <ResultRow label="Max Loan Amount"  value={fmtD(res.principal)} highlight />
        <ResultRow label="Total Repayment"  value={fmtD(res.total)}    />
        <ResultRow label="Total Interest"   value={fmtD(res.interest)} />
        <ResultRow label="Interest Rate"    value={fmtP(Number(rate))} />
      </ResultsCard>
    </CalcGrid>
  );
}

// ─── 6. Retirement Calculator ─────────────────────────────────────────────────
function RetirementCalc() {
  const [currentAge,    setCurrentAge]    = useState(30);
  const [retireAge,     setRetireAge]     = useState(65);
  const [currentSaving, setCurrentSaving] = useState(25000);
  const [monthly,       setMonthly]       = useState(500);
  const [rate,          setRate]          = useState(7.0);
  const [inflation,     setInflation]     = useState(3.0);

  const res = useMemo(() => {
    const years   = Math.max(0, Number(retireAge) - Number(currentAge));
    const r       = Number(rate) / 100 / 12;
    const n       = years * 12;
    const p       = Number(currentSaving);
    const pmt     = Number(monthly);
    const fvLump  = p * Math.pow(1 + r, n);
    const fvContrib = r === 0 ? pmt * n : pmt * (Math.pow(1 + r, n) - 1) / r;
    const total   = fvLump + fvContrib;
    const inf     = Number(inflation) / 100;
    const realVal = total / Math.pow(1 + inf, years);
    const contributed = p + pmt * n;
    return { total, realVal, fvLump, fvContrib, contributed, growth: total - contributed, years };
  }, [currentAge, retireAge, currentSaving, monthly, rate, inflation]);

  return (
    <CalcGrid>
      <Card>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
          <Field label="Current Age"    value={currentAge}    onChange={setCurrentAge}    suffix="yrs" step="1" min="16" max="80" />
          <Field label="Retirement Age" value={retireAge}     onChange={setRetireAge}     suffix="yrs" step="1" min="50" max="85" />
        </div>
        <Field label="Current Savings"       value={currentSaving} onChange={setCurrentSaving} prefix="$" />
        <Field label="Monthly Contribution"  value={monthly}       onChange={setMonthly}       prefix="$" />
        <Field label="Expected Return (p.a.)"value={rate}          onChange={setRate}          suffix="%" step="0.1" />
        <Field label="Inflation Rate"        value={inflation}     onChange={setInflation}     suffix="%" step="0.1" />
      </Card>
      <ResultsCard title={`RETIREMENT IN ${res.years} YEARS`}>
        <ResultRow label="Projected Balance"         value={fmtD(res.total)}       highlight />
        <ResultRow label="Inflation-Adjusted Value"  value={fmtD(res.realVal)}     />
        <ResultRow label="Total Contributed"         value={fmtD(res.contributed)} />
        <ResultRow label="Investment Growth"         value={fmtD(res.growth)}      />
        <ResultRow label="Monthly Income (4% rule)"  value={fmtD(res.total * 0.04 / 12)} />
      </ResultsCard>
    </CalcGrid>
  );
}

// ─── 7. Amortization Calculator ───────────────────────────────────────────────
function AmortizationCalc() {
  const [amount,  setAmount]  = useState(300000);
  const [rate,    setRate]    = useState(7.0);
  const [term,    setTerm]    = useState(30);
  const [showAll, setShowAll] = useState(false);

  const { mp, schedule } = useMemo(() => {
    const r = Number(rate) / 100 / 12;
    const n = Number(term) * 12;
    const p = Number(amount);
    if (r === 0) {
      const mp = p / n;
      const schedule = Array.from({ length: n }, (_, i) => ({
        month: i + 1, payment: mp, principal: mp, interest: 0,
        balance: p - mp * (i + 1),
      }));
      return { mp, schedule };
    }
    const mp = p * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    let balance = p;
    const schedule = [];
    for (let i = 0; i < n; i++) {
      const interest = balance * r;
      const principal = mp - interest;
      balance -= principal;
      schedule.push({ month: i + 1, payment: mp, principal, interest, balance: Math.max(0, balance) });
    }
    return { mp, schedule };
  }, [amount, rate, term]);

  const totalInterest = schedule.reduce((s, r) => s + r.interest, 0);
  const visible = showAll ? schedule : schedule.slice(0, 12);

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", marginBottom: "24px" }}>
        <Field label="Loan Amount"   value={amount} onChange={setAmount} prefix="$" />
        <Field label="Interest Rate" value={rate}   onChange={setRate}   suffix="%" step="0.01" />
        <Field label="Loan Term"     value={term}   onChange={setTerm}   suffix="yrs" step="1" min="1" max="30" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "12px", marginBottom: "24px" }}>
        {[
          { l: "Monthly Payment", v: fmtD(mp), hi: true },
          { l: "Total Interest",  v: fmtD(totalInterest) },
          { l: "Total Cost",      v: fmtD(mp * Number(term) * 12) },
        ].map(({ l, v, hi }) => (
          <div key={l} style={{ background: hi ? "rgba(212,175,55,0.08)" : "var(--bg-card)", border: `1px solid ${hi ? "rgba(212,175,55,0.25)" : "var(--border-strong)"}`, borderRadius: "12px", padding: "16px" }}>
            <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", marginBottom: "6px" }}>{l.toUpperCase()}</div>
            <div style={{ fontSize: "20px", fontWeight: "800", color: hi ? "#D4AF37" : "var(--text-primary)", letterSpacing: "-0.03em" }}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-strong)", borderRadius: "14px", overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr 1fr 1fr", padding: "12px 20px", borderBottom: "1px solid var(--border-strong)", fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em" }}>
          <span>MONTH</span><span style={{ textAlign:"right" }}>PAYMENT</span><span style={{ textAlign:"right" }}>PRINCIPAL</span><span style={{ textAlign:"right" }}>INTEREST</span><span style={{ textAlign:"right" }}>BALANCE</span>
        </div>
        {visible.map((r, i) => (
          <div key={r.month} style={{
            display: "grid", gridTemplateColumns: "80px 1fr 1fr 1fr 1fr",
            padding: "10px 20px", fontSize: "13px",
            background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)",
            borderBottom: i < visible.length - 1 ? "1px solid var(--border)" : "none",
          }}>
            <span style={{ color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace" }}>{r.month}</span>
            <span style={{ textAlign: "right", color: "var(--text-primary)" }}>{fmtD(r.payment)}</span>
            <span style={{ textAlign: "right", color: "#52D68A" }}>{fmtD(r.principal)}</span>
            <span style={{ textAlign: "right", color: "#e07070" }}>{fmtD(r.interest)}</span>
            <span style={{ textAlign: "right", color: "var(--text-muted)" }}>{fmtD(r.balance)}</span>
          </div>
        ))}
        {schedule.length > 12 && (
          <button
            onClick={() => setShowAll(p => !p)}
            style={{
              width: "100%", padding: "14px", background: "none", border: "none",
              borderTop: "1px solid var(--border-strong)", color: "#D4AF37", fontSize: "13px",
              cursor: "pointer", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "500",
            }}
          >
            {showAll ? "Show less ↑" : `Show all ${schedule.length} months ↓`}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── 8. Investment Calculator ─────────────────────────────────────────────────
function InvestmentCalc() {
  const [initial,  setInitial]  = useState(10000);
  const [monthly,  setMonthly]  = useState(500);
  const [rate,     setRate]     = useState(8.0);
  const [years,    setYears]    = useState(20);
  const [taxRate,  setTaxRate]  = useState(15);

  const res = useMemo(() => {
    const r    = Number(rate) / 100 / 12;
    const n    = Number(years) * 12;
    const p    = Number(initial);
    const pmt  = Number(monthly);
    const fvLump   = p * Math.pow(1 + r, n);
    const fvContrib = r === 0 ? pmt * n : pmt * (Math.pow(1 + r, n) - 1) / r;
    const total      = fvLump + fvContrib;
    const contributed = p + pmt * n;
    const gains       = total - contributed;
    const afterTax    = total - gains * (Number(taxRate) / 100);
    // Year-by-year snapshots for the mini chart
    const snapshots = Array.from({ length: Number(years) + 1 }, (_, yr) => {
      const nn = yr * 12;
      const fl = p * Math.pow(1 + r, nn);
      const fc = r === 0 ? pmt * nn : pmt * (Math.pow(1 + r, nn) - 1) / r;
      return { yr, value: fl + fc, contributed: p + pmt * nn };
    });
    return { total, contributed, gains, afterTax, snapshots };
  }, [initial, monthly, rate, years, taxRate]);

  const maxVal = res.snapshots[res.snapshots.length - 1].value;

  return (
    <CalcGrid>
      <Card>
        <Field label="Initial Investment"    value={initial}  onChange={setInitial}  prefix="$" />
        <Field label="Monthly Contribution"  value={monthly}  onChange={setMonthly}  prefix="$" />
        <Field label="Annual Return"         value={rate}     onChange={setRate}     suffix="%" step="0.1" />
        <Field label="Time Horizon"          value={years}    onChange={setYears}    suffix="yrs" step="1" min="1" max="50" />
        <Field label="Capital Gains Tax"     value={taxRate}  onChange={setTaxRate}  suffix="%" step="1" min="0" max="40" />
      </Card>
      <div>
        <ResultsCard title="INVESTMENT PROJECTION">
          <ResultRow label="Final Portfolio Value" value={fmtD(res.total)}       highlight />
          <ResultRow label="After-Tax Value"       value={fmtD(res.afterTax)}    />
          <ResultRow label="Total Contributed"     value={fmtD(res.contributed)} />
          <ResultRow label="Investment Gains"      value={fmtD(res.gains)}       />
          <ResultRow label="Return on Investment"  value={fmt(res.gains / res.contributed * 100) + "%"} />
        </ResultsCard>
        {/* Growth chart */}
        <div style={{ marginTop: "16px", background: "var(--bg-card)", border: "1px solid var(--border-strong)", borderRadius: "14px", padding: "20px" }}>
          <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", marginBottom: "12px" }}>PORTFOLIO GROWTH</div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: "3px", height: "80px" }}>
            {res.snapshots.filter((_, i) => i % Math.max(1, Math.floor(res.snapshots.length / 20)) === 0).map((s, i, arr) => (
              <div key={s.yr} style={{ flex: 1, display: "flex", flexDirection: "column", gap: "2px", alignItems: "stretch", height: "100%", justifyContent: "flex-end" }}>
                <div style={{ borderRadius: "2px 2px 0 0", background: "rgba(212,175,55,0.25)", height: `${s.contributed / maxVal * 100}%` }} />
                <div style={{ borderRadius: "2px 2px 0 0", background: "linear-gradient(180deg, #D4AF37, #9A7C1E)", height: `${(s.value - s.contributed) / maxVal * 100}%` }} />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px", fontSize: "11px", color: "var(--text-faint)" }}>
            <span>Year 0</span><span>Year {years}</span>
          </div>
          <div style={{ display: "flex", gap: "16px", marginTop: "8px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: "var(--text-muted)" }}><div style={{ width: "10px", height: "10px", borderRadius: "2px", background: "rgba(212,175,55,0.25)" }} />Contributions</div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: "var(--text-muted)" }}><div style={{ width: "10px", height: "10px", borderRadius: "2px", background: "#D4AF37" }} />Growth</div>
          </div>
        </div>
      </div>
    </CalcGrid>
  );
}

// ─── Calculator definitions ───────────────────────────────────────────────────
const CALCS = [
  { id: "mortgage",    label: "Mortgage",    icon: "⌂",  component: MortgageCalc    },
  { id: "loan",        label: "Loan",        icon: "◈",  component: LoanCalc        },
  { id: "auto",        label: "Auto Loan",   icon: "◉",  component: AutoLoanCalc    },
  { id: "interest",    label: "Interest",    icon: "%",  component: InterestCalc    },
  { id: "payment",     label: "Payment",     icon: "$",  component: PaymentCalc     },
  { id: "retirement",  label: "Retirement",  icon: "△",  component: RetirementCalc  },
  { id: "amortization",label: "Amortization",icon: "⊞",  component: AmortizationCalc},
  { id: "investment",  label: "Investment",  icon: "◎",  component: InvestmentCalc  },
];

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function CalculatorsPage() {
  const [active, setActive] = useState("mortgage");
  const Calc = CALCS.find(c => c.id === active).component;

  return (
    <div className="calc-page-wrap" style={{ padding: "32px 40px", maxWidth: "1200px", fontFamily: "'Capsule Sans', sans-serif" }}>
      <style>{`
        @media (max-width: 768px) {
          .calc-grid { grid-template-columns: 1fr !important; }
          .calc-page-wrap { padding: 24px 16px !important; }
          .calc-tabs { gap: 4px !important; padding: 4px !important; }
          .calc-tabs button { padding: 8px 10px !important; font-size: 12px !important; }
        }
      `}</style>
      {/* Header */}
      <div style={{ marginBottom: "32px" }}>
        <h1 style={{ fontSize: "28px", fontWeight: "800", color: "var(--text-primary)", letterSpacing: "-0.03em", marginBottom: "6px" }}>
          Financial Calculators
        </h1>
        <p style={{ fontSize: "14px", color: "var(--text-muted)" }}>
          Plan smarter with real-time calculations for every financial decision.
        </p>
      </div>

      {/* Tab strip */}
      <div className="calc-tabs" style={{
        display: "flex", gap: "6px", flexWrap: "wrap",
        background: "var(--bg-main)", padding: "6px", borderRadius: "14px",
        border: "1px solid var(--border)", marginBottom: "28px",
      }}>
        {CALCS.map(c => (
          <button
            key={c.id}
            onClick={() => setActive(c.id)}
            style={{
              display: "flex", alignItems: "center", gap: "7px",
              padding: "9px 16px", borderRadius: "10px", border: "none",
              background: active === c.id
                ? "linear-gradient(135deg, rgba(212,175,55,0.18), rgba(212,175,55,0.06))"
                : "transparent",
              color: active === c.id ? "#D4AF37" : "#777",
              fontSize: "13px", fontWeight: active === c.id ? "600" : "400",
              cursor: "pointer", fontFamily: "'Capsule Sans', sans-serif",
              transition: "all 0.15s",
              borderLeft: active === c.id ? "2px solid #D4AF37" : "2px solid transparent",
            }}
          >
            <span style={{ fontSize: "14px" }}>{c.icon}</span>
            {c.label}
          </button>
        ))}
      </div>

      {/* Active calculator */}
      <Calc key={active} />

      {/* Disclaimer */}
      <p style={{ marginTop: "32px", fontSize: "11px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace", lineHeight: "1.6" }}>
        DISCLAIMER · Results are estimates for informational purposes only. Consult a financial advisor before making financial decisions.
      </p>
    </div>
  );
}
